// Read-only route links: the /r/{token} address, and the shared route as GET /shared/{token} gives it (no account).
import { config } from '@/config';
import type { ApiTrail, LonLat, Trail } from '@/features/plan/trail';
import { toTrail } from '@/features/plan/trail';

/** Where the Xpedition2 logo goes. */
export const HOME_URL = 'https://xpedition2.com';
/** Where "Find out more" goes. */
export const ABOUT_URL = HOME_URL;
/** Sign-up: the app asks Keycloak for its registration page (AuthProvider), then opens Plan. */
export const SIGN_UP_PATH = '/plan?signup=1';

/** The page a shared route's link opens. */
export const shareUrl = (token: string) => `${window.location.origin}/r/${token}`;

/** A shared route's link (/r/{token}): open to anyone, so the app skips signing in and its shell. */
export const sharedToken = (path: string) => /^\/r\/([A-Za-z0-9_-]{16,32})\/?$/.exec(path)?.[1];

export const sharedApi = (token: string) =>
  `${config.apiUrl.replace(/\/$/, '')}/shared/${encodeURIComponent(token)}`;

/** A shared route, with the languages of the Activity Summaries its owner has kept of it (none: no PDF to offer). */
export type SharedRoute = Trail & { summaries: string[] };

export async function loadSharedRoute(token: string): Promise<SharedRoute> {
  const response = await fetch(sharedApi(token));
  if (response.status === 404) throw new Error('gone');
  if (!response.ok) throw new Error(`GET shared: ${response.status}`);
  const json = (await response.json()) as ApiTrail & { summaries?: string[]; kind?: string };
  // A shared track comes as segments (without their times).
  return { ...toTrail(json.kind === 'track' ? 'track' : 'route', json), summaries: json.summaries ?? [] };
}

/** The owner's kept Activity Summary: in the viewer's language if there is one (the API falls back otherwise). */
export const sharedSummaryUrl = (token: string, language: string) =>
  `${sharedApi(token)}/summary?lang=${encodeURIComponent(language.slice(0, 2))}`;

/**
 * The area round a shared route that Satmap Adventure tiles come from, [west, south, east, north] in degrees: its
 * bounding box with a quarter of its size each side, at least 0.02° (about 2 km). The API (RouteShareService.Area)
 * serves tiles only here, so asking for no others saves refused downloads.
 */
export function sharedArea(trail: Trail): [number, number, number, number] | undefined {
  const points: LonLat[] = trail.lines.flat();
  if (points.length === 0) return undefined;
  let [west, south, east, north] = [180, 90, -180, -90];
  for (const [lon, lat] of points) {
    west = Math.min(west, lon);
    east = Math.max(east, lon);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  }
  const padLon = Math.max((east - west) * 0.25, 0.02);
  const padLat = Math.max((north - south) * 0.25, 0.02);
  return [
    Math.max(west - padLon, -180),
    Math.max(south - padLat, -85.06),
    Math.min(east + padLon, 180),
    Math.min(north + padLat, 85.06),
  ];
}

/** The shared route as a portrait picture to post (1080 × 1350); `download` saves it as a file. */
export const sharedStoryUrl = (token: string, download = false) =>
  `${sharedApi(token)}/story.jpg${download ? '?download=true' : ''}`;

/** The shared route on the map at 1200 × 630, as link previews show it. */
export const sharedCardUrl = (token: string) => `${sharedApi(token)}/card.jpg`;

/** Text cut at a word to `max` characters, with an ellipsis when cut. */
export function shorten(text: string, max: number): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length <= max) return flat;
  const cut = flat.lastIndexOf(' ', max - 1);
  return `${flat.slice(0, cut > max / 2 ? cut : max - 1).replace(/[,.;:]$/, '')}…`;
}

/** The post: the invitation, then the route's description (if it has one). */
export const shareText = (message: string, description: string, max = 600) =>
  description.trim() ? `${message}\n\n${shorten(description, max)}` : message;

/**
 * Where a link can be posted: each service's own "share" page, filled in (no account linking). Facebook takes
 * only the link: its preview (the API's Open Graph page) shows the picture and description.
 * X counts a link as 23 characters of its 280.
 */
export interface SocialShareLink {
  id: string;
  label: string;
  href: string;
  /** Text the service won't take from us, copied to the clipboard to paste into the post. */
  pasteText?: string;
}

export function socialShareLinks(url: string, message: string, description = ''): SocialShareLink[] {
  const u = encodeURIComponent(url);
  const text = shareText(message, description);
  const both = encodeURIComponent(`${text}\n\n${url}`);
  // Bluesky allows 300 characters, the link included.
  const bluesky = encodeURIComponent(
    `${shareText(message, description, Math.max(0, 300 - message.length - url.length - 6))}\n\n${url}`,
  );
  const x = encodeURIComponent(shareText(message, description, Math.max(0, 280 - 24 - message.length - 2)));
  return [
    { id: 'whatsapp', label: 'WhatsApp', href: `https://wa.me/?text=${both}` },
    // Facebook won't take text from a website: the text is copied for pasting.
    {
      id: 'facebook',
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      pasteText: text,
    },
    { id: 'x', label: 'X', href: `https://x.com/intent/post?text=${x}&url=${u}` },
    // LinkedIn's compose box, filled in (its share-offsite page takes only the link); it previews the link.
    {
      id: 'linkedin',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/feed/?shareActive=true&text=${both}`,
      // Copied too, in case LinkedIn drops it.
      pasteText: `${text}

${url}`,
    },
    { id: 'bluesky', label: 'Bluesky', href: `https://bsky.app/intent/compose?text=${bluesky}` },
  ];
}

/**
 * The route's picture as a file for the device's share sheet, or null where it can't take files. Loaded before
 * the click: Safari only opens the sheet straight from one.
 */
export async function loadSharePicture(token: string, title: string): Promise<File | null> {
  if (!navigator.canShare) return null;
  try {
    const response = await fetch(sharedCardUrl(token));
    if (!response.ok) return null;
    const file = new File([await response.blob()], `${shorten(title, 60) || 'route'}.jpg`, {
      type: 'image/jpeg',
    });
    return navigator.canShare({ files: [file] }) ? file : null;
  } catch {
    return null;
  }
}

/**
 * The device's share sheet (phones, and Chrome or Edge on Windows): the picture (if it could be loaded), the
 * text with the description, and the link, together. Call it straight from the click.
 */
export function sharePost(title: string, text: string, url: string, picture: File | null) {
  // Some apps drop the link when a file comes with it, so it is in the text too.
  const data: ShareData = picture
    ? {
        title,
        text: `${text}

${url}`,
        files: [picture],
      }
    : { title, text, url };
  navigator.share?.(data).catch(() => {
    // Cancelled, or the app refused the picture: nothing to say.
  });
}
